import { createContext, FocusEvent, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import useForceReRender from '../hooks/useForceReRender'
import useSetFocusById from '../hooks/useSetFocus'

const keyboardControls = ['Enter', ' ', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']

type FocusEnterFn = (() => void) | null

interface KeyboardControlsContextValue {
  isEnabled: boolean
  getFocusedElement: () => string | null
  setFocusedElement: (elementId: string) => void
  isElementFocused: (elementId: string) => boolean
  focusNextElement: () => void
  focusPreviousElement: () => void
  handleNonKeyboardFocus: (event: FocusEvent) => void

  addFocusableElement: (elementId: string, isExternal: boolean, manualFocusHandling: boolean, onFocusEnterFromStart: FocusEnterFn, onFocusEnterFromEnd: FocusEnterFn) => void
  removeFocusableElement: (elementId: string) => void
}

const initialContextValue: KeyboardControlsContextValue = {
  isEnabled: false,
  getFocusedElement: () => null,
  setFocusedElement: () => {},
  isElementFocused: () => false,
  focusNextElement: () => {},
  focusPreviousElement: () => {},
  handleNonKeyboardFocus: () => {},
  addFocusableElement: () => {},
  removeFocusableElement: () => {}
}

export const KeyboardControlsContext = createContext<KeyboardControlsContextValue>(initialContextValue)

export const KeyboardControlsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const focusableElements = useRef<string[]>([])
  const externalElements = useRef<string[]>([])
  const manualFocusElements = useRef<string[]>([])
  const focusEnterFromStartFunctions = useRef<FocusEnterFn[]>([])
  const focusEnterFromEndFunctions = useRef<FocusEnterFn[]>([])
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const focused = useRef<string>('')

  const reRender = useForceReRender()
  const [setFocusById] = useSetFocusById()

  /**
   * Adds an element ID to the list of focusable elements managed by the context.
   *
   * @param {string} elementId - The ID of the element to add to the focusable elements list.
   * @param {boolean} [isExternal=false] - Set to `true` if the element is managed externally (e.g., if it’s an iframe or Unity component).
   * @param {boolean} [manualFocusHandling=false] - Required for external elements like the Unity canvas that does not allow to easily prevent default event actions.
   */
  const addFocusableElement = (elementId: string, isExternal: boolean = false, manualFocusHandling: boolean = false, onFocusEnterFromStart: FocusEnterFn = null, onFocusEnterFromEnd: FocusEnterFn = null): void => {
    focusableElements.current.push(elementId)
    focusEnterFromStartFunctions.current.push(onFocusEnterFromStart)
    focusEnterFromEndFunctions.current.push(onFocusEnterFromEnd)

    if (isExternal) {
      externalElements.current.push(elementId)
    }

    if (manualFocusHandling) {
      manualFocusElements.current.push(elementId)
    }
  }

  const removeFocusableElement = (elementId: string): void => {
    let idIndex = focusableElements.current.indexOf(elementId)
    if (idIndex !== -1) {
      focusableElements.current.splice(idIndex, 1)
      focusEnterFromStartFunctions.current.splice(idIndex, 1)
      focusEnterFromEndFunctions.current.splice(idIndex, 1)
    }

    idIndex = externalElements.current.indexOf(elementId)
    if (idIndex !== -1) {
      externalElements.current.splice(idIndex, 1)
    }

    idIndex = manualFocusElements.current.indexOf(elementId)
    if (idIndex !== -1) {
      manualFocusElements.current.splice(idIndex, 1)
    }
  }

  const setFocusedElement = useCallback((elementId: string): void => {
    focused.current = elementId
    setFocusById(elementId)
  }, [setFocusById])

  const getFocusedElement = (): string | null => {
    return focused.current
  }

  const isElementFocused = (elementId: string): boolean => {
    return elementId === focused.current
  }

  const rotateFocus = useCallback((delta: number) => {
    const elementsCount = focusableElements.current.length
    let index = focusableElements.current.indexOf(focused?.current)
    if (index < 0) { index = 0 }

    index += delta
    // Get around TS negative modulo definition to wrap around components
    index = ((index % elementsCount) + elementsCount) % elementsCount

    let onFocusEnter = null
    if (delta > 0) {
      onFocusEnter = focusEnterFromStartFunctions.current[index]
    } else if (delta < 0) {
      onFocusEnter = focusEnterFromEndFunctions.current[index]
    }
    if (onFocusEnter !== null) {
      onFocusEnter()
    }

    const newFocused = focusableElements.current[index]
    setFocusedElement(newFocused)
  }, [setFocusedElement])

  const focusNextElement = useCallback(() => {
    rotateFocus(1)
  }, [rotateFocus])

  const focusPreviousElement = useCallback(() => {
    rotateFocus(-1)
  }, [rotateFocus])

  // Sets up keyboard event handling
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      const isExternalElement = externalElements.current.includes(focused.current)
      const manualFocusHandling = manualFocusElements.current.includes(focused.current)

      const keyPressed = event.key
      if (!keyboardControls.includes(keyPressed)) {
        return
      }
      setIsEnabled(true)

      // Handle Enter and Space for focused buttons
      if (keyPressed === 'Enter' || keyPressed === ' ') {
        const isExternalElement = externalElements.current.includes(focused.current)
        const manualFocusHandling = manualFocusElements.current.includes(focused.current)
        if (!isExternalElement && !manualFocusHandling) {
          event.preventDefault()
          const element = document.getElementById(focused.current)
          if (element != null) {
            element.click()
          }
        }
      }

      // Completely control TAB and SHIFT-TAB key presses to rotate focus
      if (keyPressed === 'Tab') {
        if (manualFocusHandling) {
          event.preventDefault()
        } else
          if (!isExternalElement) {
            if (event.shiftKey) {
              focusPreviousElement()
            } else {
              focusNextElement()
            }

            event.preventDefault()
          }
      }
    }

    document.addEventListener('keydown', handleKeyPress, true)

    return () => {
      document.removeEventListener('keydown', handleKeyPress, true)
    }
  }, [setFocusedElement, focusNextElement, focusPreviousElement])

  const handleNonKeyboardFocus = useCallback((event: FocusEvent): void => {
    const id = (event.target as Element).id

    if (focusableElements.current.includes(id)) {
      focused.current = id
      reRender()
    }
  }, [focusableElements, reRender])

  return (
    <KeyboardControlsContext.Provider
      value={{
        isEnabled,
        getFocusedElement,
        setFocusedElement,
        isElementFocused,
        focusNextElement,
        focusPreviousElement,
        handleNonKeyboardFocus,
        addFocusableElement,
        removeFocusableElement
      }}
    >
      {children}
    </KeyboardControlsContext.Provider>
  )
}

export const useKeyboardControls = (): KeyboardControlsContextValue => {
  return useContext(KeyboardControlsContext)
}
